import { createContext, useContext, FormEvent, RefObject } from 'react'
import type { ChatEditorRef } from '@/components/editor/ChatEditor'
import type { Macro } from '@/components/MacroPicker'
import type { UseSnoozePickerReturn } from '@/hooks/useSnoozePicker'
import type {
  ConversationDetail,
  Agent,
  Message,
  InternalNote,
  PendingAttachment,
  WhatsAppTemplate,
} from './types'

export interface InboxContextValue {
  conversation: ConversationDetail
  messages: Message[]
  internalNotes: InternalNote[]
  currentAgent: Agent
  filter: string
  statusFilter: string
  macros: Macro[]
  whatsappTemplates: WhatsAppTemplate[]
  // Typing
  typingUsers: Array<{ user_id: number; user_name: string; user_type: string; avatar_url: string | null }>
  notifyTyping: (isTyping: boolean) => void
  // Scroll refs
  messagesEndRef: RefObject<HTMLDivElement | null>
  messagesContainerRef: RefObject<HTMLDivElement | null>
  // Conversation actions
  onCloseConversation: () => void
  onCloseWithRating: () => void
  onReopenConversation: () => void
  onUnsnoozeConversation: () => void
  // Snooze
  snoozePicker: UseSnoozePickerReturn
  snoozeButtonRef: RefObject<HTMLButtonElement | null>
  // Details panel toggle
  isDetailsPanelOpen: boolean
  onToggleDetailsPanel: () => void
  // Optimistic send
  onSendMessage: (e?: FormEvent) => void
  onRetryMessage: (tempId: string) => void
  // Persist message draft per conversation
  saveDraft: (html: string, text: string) => void
  // Editing
  onDeleteMessage: (conversationId: number, messageId: number) => void
  // State refs for message sending
  bodyRef: React.MutableRefObject<string>
  draftPlainTextRef: React.MutableRefObject<string>
  pendingAttachments: PendingAttachment[]
  setPendingAttachments: React.Dispatch<React.SetStateAction<PendingAttachment[]>>
  chatEditorRef: RefObject<ChatEditorRef | null>
  replyingToMessage: Message | null
  setReplyingToMessage: React.Dispatch<React.SetStateAction<Message | null>>
  // Optimistic note send
  onSendNote: (body: string, attachments?: File[]) => void
  onRetryNote: (tempId: string) => void
  // Image/video lightbox
  lightbox: {
    open: (images: Array<{ url: string; filename?: string }>, index?: number) => void
  }
  onVideoClick: (url: string, filename: string) => void
  // Attachment source validation
  conversationSource: string | null
}

const InboxContext = createContext<InboxContextValue | null>(null)

export function InboxProvider({ value, children }: { value: InboxContextValue; children: React.ReactNode }) {
  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>
}

export function useInboxContext(): InboxContextValue {
  const ctx = useContext(InboxContext)
  if (!ctx) throw new Error('useInboxContext must be used within an InboxProvider')
  return ctx
}
